import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Info, Loader2, Plus, Search, X } from 'lucide-react';

import {
  fetchVocabularySubtreeTerms,
  searchVocabularyTerms,
  termCategoryOf,
  type VocabTerm,
  type VocabularyDisplayDto,
} from '@/api/vocabularies';
import { cn } from '@/lib/utils';

import { InlineTermInfoPanel } from './InlineTermInfoPanel';
import { termSubtitle, termTitle } from './term-display';
import { useVocabularyInfo } from './useVocabularyInfo';

/** Picker selection — decoupled from {@link VocabTerm} so form code stays simple. */
export interface VocabularyTermValue {
  readonly id: string;
  readonly label: string;
}

interface VocabularyTermPickerProps {
  /** Which vocabulary to search (`hpo`, `hgnc`, `omim`, `ordo`). */
  readonly vocabularyId: string;
  /**
   * Display hints. Optional — when omitted, the picker reads them (plus name/version) from
   * the catalogue itself via {@link useVocabularyInfo}. Pass to override.
   */
  readonly display?: VocabularyDisplayDto | null;
  /**
   * Select mode: when `onChange` is provided, activating a row commits the selection and
   * collapses to a chip. Browse mode: omit `onChange` and activating a row opens its info.
   */
  readonly value?: VocabularyTermValue | null;
  readonly onChange?: (next: VocabularyTermValue | null) => void;
  readonly placeholder?: string;
  readonly ariaLabel?: string;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
  /** Show an "add custom term" footer when nothing matches (patient phenotype entry). */
  readonly allowCustom?: boolean;
  /** Render an info (ⓘ) action per result that expands an inline detail panel. */
  readonly showInfo?: boolean;
  /** Use a shorter (h-8) input — e.g. on the admin vocabulary cards. */
  readonly dense?: boolean;
  /** Source vocabulary name + version for the info header (defaults from the catalogue). */
  readonly vocabularyName?: string | null;
  readonly version?: string | null;
  /**
   * Restrict free-text results to a body-system category by keeping only
   * results whose `term_category` (HPO's curated category-ancestor field)
   * includes this id. Works for phenotypic-abnormality roots (large subtrees
   * under HP:0000118) where `term_category` is populated. For small
   * clinical-modifier subtrees (onset, severity, …) `term_category` is empty —
   * use {@link descendantsOf} instead. Mutually exclusive with it.
   */
  readonly subtreeRootId?: string;
  /**
   * Restrict the picker to the descendants of this term. Unlike
   * {@link subtreeRootId}, the picker materialises the whole subtree (via the
   * `is-a` children walk) and filters it locally, so it works for branches
   * that carry no `term_category` — e.g. the onset (HP:0003674) / severity
   * (HP:0012824) qualifier trees. On focus it shows the full subtree so the
   * user sees what belongs in the field. Only for small, bounded subtrees.
   * PTV2-1168.
   */
  readonly descendantsOf?: string;
  /**
   * Term ids already present in the caller's collection. Rows matching one
   * of these stay visible but render disabled with an "already added" hint
   * and can't be committed (mouse or keyboard). Used by the phenotype add
   * UX to prevent the same HPO term being attached to a patient twice
   * (PTV2-1119).
   */
  readonly excludedIds?: ReadonlySet<string>;
  /**
   * Labels (lowercased, trimmed) already present in the caller's collection.
   * Used only by the {@link allowCustom} footer: a custom-term commit is
   * blocked when the trimmed query matches one of these. Lets us catch the
   * "same label, no id" case for custom phenotype entries (PTV2-1119).
   */
  readonly excludedLabels?: ReadonlySet<string>;
}

const DEBOUNCE_MS = 200;
// Min space (px) needed below the input before the results list flips above it.
const DROPDOWN_RESERVE = 280;
/** Larger page size used when {@link VocabularyTermPickerProps.subtreeRootId} filters client-side. */
const SUBTREE_FETCH_SIZE = 100;

/** Case-insensitive match of a preloaded subtree term against the query (id / name / synonyms). */
function matchesTermQuery(term: VocabTerm, query: string): boolean {
  if (query.length === 0) return true;
  const needle = query.toLowerCase();
  if (term.id.toLowerCase().includes(needle)) return true;
  if ((term.name ?? '').toLowerCase().includes(needle)) return true;
  return (term.synonyms ?? []).some((s) => s.toLowerCase().includes(needle));
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      clearTimeout(handle);
    };
  }, [value, delayMs]);
  return debounced;
}

/**
 * Vocabulary-agnostic term search box. Queries the live vocabulary-service search endpoint
 * (debounced, relevance-ranked) and renders results using the per-vocabulary `display`
 * config (read from the catalogue). Reused on the patient phenotype entry (select mode) and
 * the admin vocabulary cards (browse mode). Empty results render an empty message; request
 * errors render a compact inline error with retry — there is no offline seed list.
 *
 * The result detail (ⓘ) expands as an inline panel anchored to its row: wider than the list
 * (overhanging right, or left when that would run off-screen), flipping above the row when it
 * wouldn't fit below, with a continuous outline tying the row and panel together.
 *
 * Keyboard: ↑/↓ move highlight, Enter activates, Esc collapses the detail then closes.
 */
export function VocabularyTermPicker({
  vocabularyId,
  display,
  value,
  onChange,
  placeholder,
  ariaLabel,
  disabled,
  autoFocus,
  allowCustom,
  showInfo,
  dense,
  vocabularyName,
  version,
  subtreeRootId,
  descendantsOf,
  excludedIds,
  excludedLabels,
}: VocabularyTermPickerProps): ReactElement {
  const { t } = useTranslation();
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const expandedRowRef = useRef<HTMLDivElement | null>(null);
  const inlinePanelRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dropUp, setDropUp] = useState(false);

  // Display config / name / version default from the catalogue; props override.
  const info = useVocabularyInfo(vocabularyId);
  const effDisplay = display ?? info?.display ?? null;
  const effName = vocabularyName ?? info?.name ?? null;
  const effVersion = version ?? info?.dataVersion ?? null;

  const selectable = onChange !== undefined;
  const trimmed = query.trim();
  const debounced = useDebounced(trimmed, DEBOUNCE_MS);

  // Two scoping modes: `descendantsOf` materialises a small subtree and
  // filters it locally (works where `term_category` is empty); otherwise the
  // free-text search endpoint drives results, optionally category-filtered by
  // `subtreeRootId`. They are mutually exclusive.
  const descendantsMode = descendantsOf !== undefined && descendantsOf.length > 0;
  // In descendants mode the dropdown shows the whole subtree on focus, so we
  // don't gate on the query being non-empty.
  const hasQueryScope = descendantsMode || debounced.length > 0;

  const search = useQuery({
    queryKey: ['vocabulary', vocabularyId, 'search', debounced, subtreeRootId ?? ''],
    queryFn: ({ signal }) =>
      searchVocabularyTerms(vocabularyId, debounced, {
        signal,
        ...(subtreeRootId !== undefined && subtreeRootId.length > 0
          ? { size: SUBTREE_FETCH_SIZE }
          : {}),
      }),
    enabled: !descendantsMode && debounced.length > 0,
    staleTime: 30_000,
  });

  const subtree = useQuery({
    queryKey: ['vocabulary', vocabularyId, 'subtree', descendantsOf ?? ''],
    queryFn: ({ signal }) =>
      fetchVocabularySubtreeTerms(vocabularyId, descendantsOf ?? '', signal),
    enabled: descendantsMode && open,
    staleTime: 5 * 60_000,
  });

  const activeQuery = descendantsMode ? subtree : search;

  const results: readonly VocabTerm[] = useMemo(() => {
    if (descendantsMode) {
      return (subtree.data ?? []).filter((term) => matchesTermQuery(term, debounced));
    }
    if (debounced.length === 0) return [];
    const raw = search.data ?? [];
    if (subtreeRootId === undefined || subtreeRootId.length === 0) return raw;
    return raw.filter((term) => {
      if (term.id === subtreeRootId) return true;
      return termCategoryOf(term).includes(subtreeRootId);
    });
  }, [descendantsMode, subtree.data, search.data, debounced, subtreeRootId]);
  const canAddCustom = allowCustom === true && selectable && trimmed.length > 0;
  const customAlreadyAdded =
    canAddCustom && excludedLabels?.has(trimmed.toLowerCase()) === true;
  const totalRows = results.length + (canAddCustom ? 1 : 0);
  const isExcluded = (term: VocabTerm): boolean =>
    excludedIds?.has(term.id) === true;
  const expandedTerm =
    expandedId !== null ? (results.find((tm) => tm.id === expandedId) ?? null) : null;

  useEffect(() => {
    setHighlighted(0);
    setExpandedId(null);
  }, [debounced]);

  useEffect(() => {
    if (autoFocus === true) inputRef.current?.focus();
  }, [autoFocus]);

  // Collapse any open detail when the dropdown closes, so reopening the search starts clean
  // rather than re-showing the previously expanded term.
  useEffect(() => {
    if (!open) setExpandedId(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      // The inline detail panel is portalled to <body>, so it's outside containerRef —
      // ignore clicks landing in it (otherwise reading it would close the whole dropdown).
      if (containerRef.current?.contains(target) === true) return;
      if (inlinePanelRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Flip the results list above the input when it would otherwise run off the bottom.
  useLayoutEffect(() => {
    if (!open) return;
    const recompute = (): void => {
      const el = containerRef.current;
      if (el === null) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < DROPDOWN_RESERVE && rect.top > spaceBelow);
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [open, results.length, expandedId]);

  const commit = (next: VocabularyTermValue): void => {
    onChange?.(next);
    setQuery('');
    setOpen(false);
  };

  const toggleInfo = (term: VocabTerm): void => {
    setExpandedId((prev) => (prev === term.id ? null : term.id));
  };

  const activateTerm = (term: VocabTerm): void => {
    if (selectable) {
      if (isExcluded(term)) return;
      commit({ id: term.id, label: termTitle(term, effDisplay) });
    } else {
      toggleInfo(term);
    }
  };

  const commitHighlight = (): void => {
    if (highlighted < results.length) {
      const term = results[highlighted];
      if (term !== undefined) activateTerm(term);
      return;
    }
    if (canAddCustom && !customAlreadyAdded) commit({ id: '', label: trimmed });
  };

  const clear = (): void => {
    onChange?.(null);
    setQuery('');
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      setHighlighted((prev) => Math.min(prev + 1, Math.max(totalRows - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      commitHighlight();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (expandedId !== null) setExpandedId(null);
      else setOpen(false);
    }
  };

  // Selected-chip view (select mode only).
  if (selectable && value != null) {
    return (
      <div
        ref={containerRef}
        className={cn(
          'flex items-center gap-2 rounded-md border border-border bg-card pl-2 pr-1 py-1',
          disabled === true && 'opacity-60',
        )}
      >
        <span className="min-w-0 flex flex-col leading-tight flex-1">
          <span className="text-[13px] font-medium text-foreground truncate">{value.label}</span>
          {value.id.length > 0 && (
            <span className="text-[11px] text-muted-foreground truncate">{value.id}</span>
          )}
        </span>
        <button
          type="button"
          onClick={clear}
          disabled={disabled === true}
          aria-label={t('vocab.picker.clear')}
          className="h-6 w-6 shrink-0 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border border-border bg-card px-2.5 focus-within:ring-2 focus-within:ring-ring',
          dense === true ? 'h-8' : 'h-9',
        )}
      >
        <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          disabled={disabled === true}
          placeholder={placeholder ?? t('vocab.picker.placeholder')}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-label={ariaLabel ?? t('vocab.picker.ariaLabel')}
          aria-activedescendant={
            open && totalRows > 0 ? `${listboxId}-option-${String(highlighted)}` : undefined
          }
          className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground"
        />
        {activeQuery.isFetching && hasQueryScope && (
          <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin shrink-0" aria-hidden="true" />
        )}
      </div>

      {open && hasQueryScope && (
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          className={cn(
            'absolute z-50 left-0 right-0 max-h-64 rounded-md border border-border bg-popover shadow-lg',
            // Lock the list's scroll while an inline detail box is open so the row it's
            // anchored to can't move out from under it.
            expandedId !== null ? 'overflow-hidden' : 'overflow-y-auto',
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          {activeQuery.isError && (
            <div className="px-3 py-2 text-xs">
              <p className="text-destructive">{t('vocab.picker.error')}</p>
              <button
                type="button"
                onClick={() => {
                  activeQuery.refetch().catch(() => undefined);
                }}
                className="mt-1 text-primary hover:underline"
              >
                {t('vocab.picker.retry')}
              </button>
            </div>
          )}

          {!activeQuery.isError && !activeQuery.isFetching && results.length === 0 && !canAddCustom && (
            <p className="px-3 py-2 text-xs text-muted-foreground">{t('vocab.picker.empty')}</p>
          )}

          {results.map((term, idx) => {
            const title = termTitle(term, effDisplay);
            const subtitle = termSubtitle(term, effDisplay) ?? term.synonyms?.join(', ') ?? null;
            const isExpanded = expandedId === term.id;
            const excluded = isExcluded(term);
            return (
              <div
                key={term.id}
                ref={isExpanded ? expandedRowRef : null}
                className="border-b border-border/50 last:border-b-0"
              >
                <div
                  className={cn(
                    'flex items-stretch transition-colors',
                    excluded
                      ? 'bg-muted/40'
                      : idx === highlighted
                        ? 'bg-accent'
                        : 'hover:bg-accent/60',
                  )}
                >
                  <button
                    type="button"
                    id={`${listboxId}-option-${String(idx)}`}
                    role="option"
                    aria-selected={idx === highlighted}
                    aria-disabled={excluded || undefined}
                    aria-expanded={showInfo === true ? isExpanded : undefined}
                    onMouseEnter={() => {
                      setHighlighted(idx);
                    }}
                    onMouseDown={(event) => {
                      // Commit on mousedown so focus handling doesn't race the click.
                      event.preventDefault();
                      activateTerm(term);
                    }}
                    className={cn(
                      'min-w-0 flex-1 text-left px-3 py-2 flex items-start justify-between gap-3',
                      excluded && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <span className="min-w-0 flex flex-col leading-tight">
                      <span
                        className={cn(
                          'text-[13px] font-medium truncate',
                          excluded ? 'text-muted-foreground' : 'text-foreground',
                        )}
                      >
                        {title}
                      </span>
                      {subtitle !== null && subtitle !== '' && (
                        <span className="text-[11px] text-muted-foreground truncate">{subtitle}</span>
                      )}
                    </span>
                    <span className="shrink-0 mt-0.5 flex flex-col items-end leading-tight">
                      <span className="text-[11px] font-mono text-muted-foreground">{term.id}</span>
                      {excluded && (
                        <span className="text-[10px] font-medium text-muted-foreground italic">
                          {t('vocab.picker.alreadyAdded')}
                        </span>
                      )}
                    </span>
                  </button>
                  {showInfo === true && (
                    <button
                      type="button"
                      aria-label={t('vocab.picker.info', { term: title })}
                      aria-expanded={isExpanded}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleInfo(term);
                      }}
                      className={cn(
                        // Extra right padding keeps the icon clear of the list's scrollbar (when
                        // results overflow), so a click lands on the icon and not the scrollbar.
                        'shrink-0 pl-2 pr-4 flex items-center text-muted-foreground hover:text-foreground',
                        isExpanded && 'text-foreground',
                      )}
                    >
                      <Info className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {canAddCustom && (
            <button
              type="button"
              id={`${listboxId}-option-${String(results.length)}`}
              role="option"
              aria-selected={highlighted === results.length}
              aria-disabled={customAlreadyAdded || undefined}
              onMouseEnter={() => {
                setHighlighted(results.length);
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                if (customAlreadyAdded) return;
                commit({ id: '', label: trimmed });
              }}
              className={cn(
                'w-full text-left px-3 py-2 flex items-center gap-2 transition-colors border-t border-border',
                customAlreadyAdded
                  ? 'bg-muted/40 cursor-not-allowed opacity-60'
                  : highlighted === results.length
                    ? 'bg-accent'
                    : 'hover:bg-accent/60',
              )}
            >
              <Plus
                className={cn(
                  'h-3.5 w-3.5 text-muted-foreground',
                  customAlreadyAdded && 'opacity-60',
                )}
                aria-hidden="true"
              />
              <span
                className={cn(
                  'text-[13px] truncate',
                  customAlreadyAdded ? 'text-muted-foreground' : 'text-foreground',
                )}
              >
                {t('vocab.picker.addCustom', { query: trimmed })}
              </span>
              {customAlreadyAdded && (
                <span className="ml-auto text-[10px] font-medium text-muted-foreground italic shrink-0">
                  {t('vocab.picker.alreadyAdded')}
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Inline detail panel — sized to the results list, hung off the expanded row, flipping
          above when it wouldn't fit below. Portalled + fixed so it isn't clipped. */}
      {open && expandedTerm !== null && (
        <InlineTermInfoPanel
          term={expandedTerm}
          display={effDisplay}
          vocabularyName={effName}
          version={effVersion}
          widthAnchorRef={listboxRef}
          positionAnchorRef={expandedRowRef}
          panelRef={inlinePanelRef}
        />
      )}
    </div>
  );
}
